import React, { Fragment, useEffect, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";

import {
  DeleteIssueReceive,
  GetIssueReceives
} from "../../../../api/medicalStore/transaction/issueReceiveAPI";

const ListIssueReceiveEntry = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  /* ================= FETCH DATA ================= */
  const fetchData = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetIssueReceives();

      if (response.success) {
        const formattedData = response.data.map((item, index) => {
          const totalQty =
            item.productDetails?.reduce(
              (sum, p) => sum + (p.quantity || 0),
              0
            ) || 0;

          return {
            index: index + 1,
            id: item._id,

            vrType: item.vrType || "-",
            vrNo: item.vrNo || "-",
            vrDate: item.vrDate
              ? new Date(item.vrDate).toLocaleDateString()
              : "-",

            fromDepartment: item.departmentId?.name || "-",
            toDepartment: item.toDepartmentId?.name || "-",

            totalItems: item.productDetails?.length || 0,
            totalQuantity: totalQty,

            grossAmount: item.grossAmount || 0,
            netAmount: item.netAmount || 0,

            remark: item.remark || "-",

            createdBy: item.createdBy?.name || "-",
            createdAt: new Date(item.createdAt).toLocaleDateString()
          };
        });

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message || "Something went wrong");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ================= SEARCH ================= */
  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  /* ================= ACTIONS ================= */
  const handleEditClick = id => {
    navigate(
      `/medical-store/transaction/issue-receive-entry/edit/${id}`
    );
  };

  const handleDeleteClick = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will soft delete the Issue / Receive entry!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteIssueReceive(id);
          if (response.success) {
            showToast("success", response.message);
            setData(prev => prev.filter(item => item.id !== id));
            setFilteredData(prev => prev.filter(item => item.id !== id));
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  /* ================= TABLE COLUMNS ================= */
  const columns = [
    { name: "S.No", selector: row => row.index, width: "5%" },

    {
      name: "VR Type",
      selector: row => row.vrType,
      sortable: true, width: "10%"
    },

    { name: "VR No", selector: row => row.vrNo, sortable: true, width: "10%" },
    { name: "VR Date", selector: row => row.vrDate, sortable: true, width: "10%" },

    {
      name: "From Dept",
      selector: row => row.fromDepartment,
      sortable: true, width: "10%"
    },
    {
      name: "To Dept",
      selector: row => row.toDepartment,
      sortable: true, width: "10%"
    },

    {
      name: "Items",
      selector: row => row.totalItems,
      sortable: true,
      width: "10%"
    },
    {
      name: "Quantity",
      selector: row => row.totalQuantity,
      sortable: true,
      width: "10%"
    },

    {
      name: "Gross Amt",
      selector: row => row.grossAmount,
      sortable: true,
      width: "10%"
    },
    {
      name: "Net Amt",
      selector: row => row.netAmount,
      sortable: true,
      width: "10%"
    },

    {
      name: "Remark",
      selector: row => row.remark,
      wrap: true,
      width: "10%"
    },

    {
      name: "Created By",
      selector: row => row.createdBy,
      sortable: true, width: "10%"
    },
    {
      name: "Created At",
      selector: row => row.createdAt,
      sortable: true, width: "10%"
    },

    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      button: true,
      width: "15%"
    }
  ];

  /* ================= CSV EXPORT ================= */
  const headers = [
    { label: "S.No", key: "index" },
    { label: "VR Type", key: "vrType" },
    { label: "VR No", key: "vrNo" },
    { label: "VR Date", key: "vrDate" },
    { label: "From Department", key: "fromDepartment" },
    { label: "To Department", key: "toDepartment" },
    { label: "Total Items", key: "totalItems" },
    { label: "Total Quantity", key: "totalQuantity" },
    { label: "Gross Amount", key: "grossAmount" },
    { label: "Net Amount", key: "netAmount" },
    { label: "Remark", key: "remark" },
    { label: "Created By", key: "createdBy" },
    { label: "Created At", key: "createdAt" }
  ];

  const ExportCSV = ({ data }) => (
    <CSVLink data={data} headers={headers} filename="issue-receive-list.csv">
      <button className="btn btn-primary">Export</button>
    </CSVLink>
  );

  /* ================= UI ================= */
  return (
    <Fragment>
      <Breadcrumb parent="Medical Store" title="Issue / Receive List" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between">
              <h5>Issue / Receive Entries</h5>

              <Link
                to={`/medical-store/transaction/issue-receive-entry/add`}
              >
                <button className="btn btn-primary">
                  Create Issue / Receive
                </button>
              </Link>
            </div>

            <div className="row mt-2">
              <div className="col-sm-6">
                <input
                  className="form-control"
                  placeholder="Search Issue / Receive..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="col-sm-6 text-end">
                <ExportCSV data={filteredData} />
              </div>
            </div>
          </div>

          <div className="card-body">
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              dense
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListIssueReceiveEntry;
